import React, { useContext, useEffect, useState, useRef } from "react";
import {
    Container,
    Button,
    Row,
    Col,
    Input,
    Label,
    Card,
    CardBody,
    CardHeader,
    Table,
    Badge,
    Alert
} from "reactstrap";
import PaginationMaterial from "../../components/PaginationMaterial/PaginationMaterial";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Header from "../../components/Headers/Header";
import AdminNavbar from "../../components/Navbars/AdminNavbar/AdminNavbar";
import { AuthContext } from "../../context/AuthContext";
import { AlunoContext } from "../../context/AlunoContext";
import { EditAlunoModal } from "../../components/Aluno/EditAlunoModal";
import { AlunoDetalhadoType, AlunoType, EditAlunoType, HistoricoAlbumAlunoType } from "../../context/hooks/useAluno";

const AlunoDetalhado = () => {
    const { objUserTk } = useContext(AuthContext);
    const { getAlunosDetalhadosPaged, alunoDetalhadoPageCount, handleGetAlunoById, handleGetHistoricoAlbumPorAluno } = useContext(AlunoContext);

    const [loading, setLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [alunoParaEdicao, setAlunoParaEdicao] = useState<EditAlunoType | null>(null);
    const [contratoIdEdicao, setContratoIdEdicao] = useState<number | undefined>(undefined);
    const [cursoIdEdicao, setCursoIdEdicao] = useState<number | undefined>(undefined);

    const [tipoFiltro, setTipoFiltro] = useState<string>("codigo");
    const [termoBusca, setTermoBusca] = useState<string>("");
    const [hasBuscado, setHasBuscado] = useState<boolean>(false);

    // Lista de resultados encontrados na pesquisa
    const [resultadosBusca, setResultadosBusca] = useState<AlunoDetalhadoType[]>([]);
    // Aluno selecionado para exibição detalhada
    const [alunoDetalheItem, setAlunoDetalheItem] = useState<AlunoDetalhadoType | null>(null);
    const [alunoCadastro, setAlunoCadastro] = useState<AlunoType | null>(null);
    const [carregandoDetalhe, setCarregandoDetalhe] = useState<boolean>(false);
    const [historicoAlbum, setHistoricoAlbum] = useState<HistoricoAlbumAlunoType[]>([]);
    const [carregandoHistorico, setCarregandoHistorico] = useState<boolean>(false);

    // Ref para rolar até a ficha de detalhes do aluno quando visualizado
    const detalhesRef = useRef<HTMLDivElement>(null);

    // Seleciona um aluno da busca para carregar os dados cadastrais completos e histórico
    const selecionarAlunoParaDetalhes = async (item: AlunoDetalhadoType) => {
        setAlunoDetalheItem(item);
        setCarregandoDetalhe(true);
        setCarregandoHistorico(true);

        // Auto-scroll imediato para a ficha detalhada ficar visível na tela
        setTimeout(() => {
            detalhesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);

        try {
            const cadPromise = handleGetAlunoById(item.alunoId);
            const histPromise = handleGetHistoricoAlbumPorAluno(item.alunoId);
            const [cad, hist] = await Promise.all([cadPromise, histPromise]);
            setAlunoCadastro(cad);
            setHistoricoAlbum(hist || []);
        } catch (error) {
            console.error("Erro ao carregar dados completos do aluno:", error);
        } finally {
            setCarregandoDetalhe(false);
            setCarregandoHistorico(false);
        }
    };

    const executarBusca = async (termo: string, tipo: string, page = 1) => {
        if (!objUserTk?.EmpresaId) return;

        const termoTrim = termo.trim();
        if (!termoTrim) {
            return;
        }

        let nameFilter: string | undefined = undefined;
        let alunoIdFilter: number | undefined = undefined;
        let cidadeFilter: string | undefined = undefined;

        if (tipo === "codigo") {
            alunoIdFilter = parseInt(termoTrim) || undefined;
        } else if (tipo === "nome") {
            nameFilter = termoTrim;
        } else if (tipo === "cidade") {
            cidadeFilter = termoTrim;
        }

        setLoading(true);
        setHasBuscado(true);
        setAlunoDetalheItem(null);
        setAlunoCadastro(null);

        try {
            const res = await getAlunosDetalhadosPaged(
                page,
                objUserTk.EmpresaId,
                10,
                nameFilter,
                alunoIdFilter,
                undefined,
                undefined,
                undefined,
                cidadeFilter,
                undefined
            );

            const lista = res || [];
            setResultadosBusca(lista);

            // Se retornou exatamente 1 aluno, já carrega a ficha detalhada automaticamente
            if (lista.length === 1) {
                await selecionarAlunoParaDetalhes(lista[0]);
            }
        } catch (error) {
            console.error("Erro ao pesquisar alunos:", error);
            setResultadosBusca([]);
        } finally {
            setLoading(false);
        }
    };

    // Busca automática com debounce ao digitar
    useEffect(() => {
        if (!objUserTk?.EmpresaId) return;

        const termoTrim = termoBusca.trim();
        if (!termoTrim) {
            setHasBuscado(false);
            setResultadosBusca([]);
            setAlunoDetalheItem(null);
            setAlunoCadastro(null);
            return;
        }

        // Para código, aguardar pelo menos 1 dígito; para nome/cidade, 2 ou mais caracteres para não disparar consultas muito pesadas
        if (tipoFiltro !== "codigo" && termoTrim.length < 2) {
            return;
        }

        const timer = setTimeout(() => {
            executarBusca(termoTrim, tipoFiltro, 1);
        }, 500);

        return () => clearTimeout(timer);
    }, [termoBusca, tipoFiltro, objUserTk?.EmpresaId]);

    const onChangePagination = async (e: any, value: number) => {
        executarBusca(termoBusca, tipoFiltro, value);
    };

    const handleClear = () => {
        setTermoBusca("");
        setTipoFiltro("codigo");
        setHasBuscado(false);
        setResultadosBusca([]);
        setAlunoDetalheItem(null);
        setAlunoCadastro(null);
    };

    const handleAbrirEdicao = async (alunoId: number, contratoId?: number, cursoId?: number) => {
        const cad = alunoCadastro?.id === alunoId ? alunoCadastro : await handleGetAlunoById(alunoId);
        if (!cad) return;
        setAlunoParaEdicao({
            id: cad.id,
            referenceCode: cad.referenceCode,
            name: cad.name,
            email: cad.email,
            cpf: cad.cpf,
            address: cad.address,
            addressNumber: cad.addressNumber,
            neighborhood: cad.neighborhood,
            city: cad.city,
            state: cad.state,
            zipCode: cad.zipCode,
            landline: cad.landline,
            telephone_01: cad.telephone_01,
            telephone_02: cad.telephone_02,
            telephone_03: cad.telephone_03,
            telephone_04: cad.telephone_04,
            telephone_05: cad.telephone_05,
            telephone_06: cad.telephone_06,
            facebook: cad.facebook,
            instagram: cad.instagram,
            keepThePhotos: cad.keepThePhotos,
            hasAlbum: cad.hasAlbum,
            numberOfPhotos: cad.numberOfPhotos,
            base64Image: cad.base64Image,
            observacao: cad.observacao,
        });
        setContratoIdEdicao(contratoId);
        setCursoIdEdicao(cursoId);
        setEditModalOpen(true);
    };

    const handleAlunoAtualizado = async () => {
        if (alunoDetalheItem) {
            const [cad, hist] = await Promise.all([
                handleGetAlunoById(alunoDetalheItem.alunoId),
                handleGetHistoricoAlbumPorAluno(alunoDetalheItem.alunoId)
            ]);
            setAlunoCadastro(cad);
            setHistoricoAlbum(hist || []);
        }
    };

    const getRelacaoStatusLabel = (status?: number) => {
        switch (Number(status)) {
            case 1:
                return "Em rota";
            case 2:
                return "Roteiro";
            case 3:
                return "Retorno";
            case 4:
                return "Devolvido";
            case 5:
                return "Vendido";
            case 6:
                return "Descartado";
            case 7:
                return "Recusou Álbum";
            default:
                return status ? `Status ${status}` : "Processado";
        }
    };

    const getStatusBadgeClass = (status?: number) => {
        switch (Number(status)) {
            case 5:
                return "success";
            case 7:
            case 6:
                return "danger";
            case 1:
            case 2:
                return "info";
            default:
                return "primary";
        }
    };

    const getPlaceholderBusca = () => {
        switch (tipoFiltro) {
            case "codigo":
                return "Digite o código do aluno...";
            case "nome":
                return "Digite o nome do aluno...";
            case "cidade":
                return "Digite a cidade...";
            default:
                return "Pesquisar...";
        }
    };

    // Monta o endereço completo com número
    const formatEnderecoCompleto = () => {
        const rua = alunoCadastro?.address || "";
        const num = alunoCadastro?.addressNumber || "";
        if (rua && num) return `${rua}, ${num}`;
        return rua || num || "";
    };

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText="Álbum dos Alunos" search={false} titleSearch="" />
            <Container fluid className="mt-2 px-2">
                {/* Card de Busca conforme imagem */}
                <Card className="shadow-sm border-0 mb-3 p-3">
                    <h3 className="text-primary font-weight-bold mb-1" style={{ color: '#0d2b45', fontSize: '1.25rem' }}>Aluno detalhado</h3>
                    <p className="text-muted mb-3 small">Pesquise um formando para visualizar suas informações.</p>

                    <Row className="align-items-end">
                        <Col xs="12" sm="4" md="3" lg="2" className="mb-3">
                            <Label className="form-control-label font-weight-bold" for="tipoFiltro">Filtrar por</Label>
                            <Input
                                className="form-control-alternative"
                                type="select"
                                id="tipoFiltro"
                                value={tipoFiltro}
                                onChange={(e) => setTipoFiltro(e.target.value)}
                            >
                                <option value="codigo">Código</option>
                                <option value="nome">Nome</option>
                                <option value="cidade">Cidade</option>
                            </Input>
                        </Col>

                        <Col xs="12" sm="8" md="5" lg="6" className="mb-3">
                            <Label className="form-control-label font-weight-bold" for="termoBusca">Pesquisa</Label>
                            <Input
                                className="form-control-alternative"
                                type={tipoFiltro === "codigo" ? "number" : "text"}
                                id="termoBusca"
                                placeholder={getPlaceholderBusca()}
                                value={termoBusca}
                                onChange={(e) => setTermoBusca(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        executarBusca(termoBusca, tipoFiltro, 1);
                                    }
                                }}
                            />
                        </Col>

                        <Col xs="12" md="4" lg="4" className="d-flex justify-content-start justify-content-md-end mb-3 align-items-end" style={{ gap: '8px' }}>
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#32325d" }}
                                onClick={() => executarBusca(termoBusca, tipoFiltro, 1)}
                                disabled={loading}
                            >
                                <i className="fa-solid fa-magnifying-glass mr-1"></i> Buscar
                            </Button>
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#f5365c" }}
                                onClick={handleClear}
                                disabled={loading}
                            >
                                <i className="fa-solid fa-trash-can mr-1"></i> Limpar
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Caso 1: Ainda não buscou */}
                {!hasBuscado && (
                    <Card className="shadow-sm border-0 mb-4 p-5 text-center text-muted" style={{ backgroundColor: "#fcfdfe" }}>
                        <i className="fa-solid fa-user-graduate fa-3x mb-3" style={{ opacity: 0.35 }}></i>
                        <h4 className="font-weight-normal text-muted">Aguardando pesquisa</h4>
                        <p className="small mb-0">Selecione o filtro e preencha o campo de busca acima para visualizar as informações do formando.</p>
                    </Card>
                )}

                {/* Caso 2: Buscou, mas nenhum formando foi localizado */}
                {hasBuscado && !loading && resultadosBusca.length === 0 && (
                    <Alert color="warning" className="shadow-sm border-0 d-flex align-items-center mb-4">
                        <i className="fa-solid fa-circle-exclamation mr-2 fa-lg"></i>
                        <div>
                            <strong>Nenhum formando localizado!</strong> Verifique o termo pesquisado e o tipo de filtro selecionado.
                        </div>
                    </Alert>
                )}

                {/* Caso 3: Múltiplos resultados encontrados (busca por nome ou cidade) */}
                {hasBuscado && resultadosBusca.length > 1 && (
                    <Card className="shadow-sm border-0 mb-3">
                        <CardHeader className="bg-transparent border-0 d-flex justify-content-between align-items-center py-2 px-3">
                            <h3 className="mb-0 text-primary" style={{ fontSize: '1rem' }}>
                                <i className="fa-solid fa-list mr-2"></i>
                                Formandos encontrados ({resultadosBusca.length})
                            </h3>
                            <small className="text-muted">Clique em um formando para abrir a ficha completa</small>
                        </CardHeader>
                        <CardBody className="pt-1 px-3 pb-3">
                            {alunoDetalheItem && (
                                <div className="alert alert-info py-2 px-3 mb-2 d-flex justify-content-between align-items-center" style={{ fontSize: '13px' }}>
                                    <span>
                                        <i className="fa-solid fa-user-check mr-2"></i>
                                        Visualizando ficha de: <strong>{alunoDetalheItem.alunoNome}</strong>
                                    </span>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        onClick={() => detalhesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                        style={{ fontSize: '11px', padding: '3px 8px' }}
                                    >
                                        <i className="fa-solid fa-arrow-down mr-1"></i> Ir para a ficha
                                    </Button>
                                </div>
                            )}
                            <Table hover responsive size="sm" className="table-striped mb-0" style={{ fontSize: '12px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ padding: '6px 8px' }}>Código</th>
                                        <th style={{ padding: '6px 8px' }}>Nome</th>
                                        <th style={{ padding: '6px 8px' }}>Curso</th>
                                        <th style={{ padding: '6px 8px' }}>Cidade</th>
                                        <th style={{ padding: '6px 8px' }}>Contrato</th>
                                        <th style={{ padding: '6px 8px' }}>Vendedor</th>
                                        <th style={{ padding: '6px 8px' }}>Status</th>
                                        <th style={{ width: "120px", padding: '6px 8px' }}>Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resultadosBusca.map((item) => {
                                        const isSelected = alunoDetalheItem?.alunoId === item.alunoId;
                                        return (
                                            <tr
                                                key={item.alunoId}
                                                style={{ cursor: "pointer", backgroundColor: isSelected ? "#e8f0fe" : undefined }}
                                                onClick={() => selecionarAlunoParaDetalhes(item)}
                                            >
                                                <td className="font-weight-bold" style={{ padding: '5px 8px' }}>{item.referenceCode || item.alunoId}</td>
                                                <td style={{ padding: '5px 8px' }}>{item.alunoNome}</td>
                                                <td style={{ padding: '5px 8px' }}>{item.cursoNome || "-"}</td>
                                                <td style={{ padding: '5px 8px' }}>{item.cidade || "-"}</td>
                                                <td style={{ padding: '5px 8px' }}>{item.contratoId || "-"}</td>
                                                <td style={{ padding: '5px 8px' }}>{item.vendedorNome || item.vendedorId || "-"}</td>
                                                <td style={{ padding: '5px 8px' }}>
                                                    <Badge color={getStatusBadgeClass(item.status)}>
                                                        {getRelacaoStatusLabel(item.status)}
                                                    </Badge>
                                                </td>
                                                <td style={{ padding: '5px 8px' }}>
                                                    <Button
                                                        size="sm"
                                                        color={isSelected ? "success" : "primary"}
                                                        onClick={(e) => {
                                                             e.stopPropagation();
                                                             selecionarAlunoParaDetalhes(item);
                                                        }}
                                                        style={{ fontSize: '11px', padding: '3px 8px' }}
                                                    >
                                                        <i className="fa-solid fa-address-card mr-1"></i>
                                                        {isSelected ? "Visualizando" : "Visualizar"}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>

                            {alunoDetalhadoPageCount > 1 && (
                                <div className="d-flex justify-content-end mt-3">
                                    <PaginationMaterial
                                        onChange={onChangePagination}
                                        count={alunoDetalhadoPageCount}
                                    />
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}

                {/* Caso 4: Aluno selecionado para visualização em formato de ficha/card (conforme o print do usuário) */}
                {alunoDetalheItem && (
                    <div ref={detalhesRef} id="secao-detalhes-aluno">
                        <Card className="shadow-sm border-0 mb-3 p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: "10px" }}>
                                <div>
                                    <h3 className="text-primary font-weight-bold mb-1" style={{ color: '#0d2b45', fontSize: '1.25rem' }}>
                                        Informações do aluno
                                    </h3>
                                    <p className="text-muted small mb-0">
                                        Dados cadastrais e informações detalhadas do formando
                                    </p>
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                    {resultadosBusca.length > 1 && (
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        >
                                            <i className="fa-solid fa-arrow-up mr-1"></i> Voltar à pesquisa
                                        </Button>
                                    )}
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleAbrirEdicao(alunoDetalheItem.alunoId, alunoDetalheItem.contratoId, alunoDetalheItem.cursoId)}
                                    >
                                        <i className="fa-solid fa-pen-to-square mr-1"></i> Editar Informações
                                    </Button>
                                </div>
                            </div>

                        {carregandoDetalhe && (
                            <div className="text-center py-4">
                                <i className="fa-solid fa-spinner fa-spin fa-2x text-primary mb-2"></i>
                                <div className="small text-muted">Carregando detalhes do formando...</div>
                            </div>
                        )}

                        {!carregandoDetalhe && (
                            <div>
                                {/* Linha 1: Código, Contrato, Vendedor, Status */}
                                <Row>
                                    <Col xs="12" sm="6" md="3" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Código</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.referenceCode || alunoDetalheItem.referenceCode || alunoDetalheItem.alunoId || ""}
                                        />
                                    </Col>
                                    <Col xs="12" sm="6" md="3" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Contrato</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoDetalheItem.contratoId || ""}
                                        />
                                    </Col>
                                    <Col xs="12" sm="6" md="3" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Vendedor</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={
                                                alunoDetalheItem.vendedorNome
                                                    ? `${alunoDetalheItem.vendedorId ? alunoDetalheItem.vendedorId + " - " : ""}${alunoDetalheItem.vendedorNome}`
                                                    : (alunoDetalheItem.vendedorId || "")
                                            }
                                        />
                                    </Col>
                                    <Col xs="12" sm="6" md="3" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Status</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={getRelacaoStatusLabel(alunoDetalheItem.status)}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 2: Nome do aluno, Quantidade de fotos */}
                                <Row>
                                    <Col xs="12" md="9" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Nome do aluno</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.name || alunoDetalheItem.alunoNome || ""}
                                        />
                                    </Col>
                                    <Col xs="12" md="3" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Quantidade de fotos</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={
                                                alunoCadastro?.numberOfPhotos != null
                                                    ? alunoCadastro.numberOfPhotos
                                                    : (alunoDetalheItem.numeroFotos != null ? alunoDetalheItem.numeroFotos : "")
                                            }
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 3: CPF */}
                                <Row>
                                    <Col xs="12" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">CPF</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.cpf || alunoDetalheItem.cpf || ""}
                                        />
                                    </Col>
                                    <Col xs="12" md="8" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Curso</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.cursoName || alunoDetalheItem.cursoNome || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 4: Endereço */}
                                <Row>
                                    <Col xs="12" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Endereço</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={formatEnderecoCompleto()}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 5: Bairro */}
                                <Row>
                                    <Col xs="12" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Bairro</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.neighborhood || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 6: Cidade */}
                                <Row>
                                    <Col xs="12" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Cidade</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.city || alunoDetalheItem.cidade || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 7: Estado, Cep */}
                                <Row>
                                    <Col xs="12" sm="6" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Estado</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.state || ""}
                                        />
                                    </Col>
                                    <Col xs="12" sm="6" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Cep</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.zipCode || ""}
                                        />
                                    </Col>
                                    <Col xs="12" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Instituição</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoDetalheItem.instituicaoNome || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 8: Telefone Fixo, Telefone 2, Telefone 3 */}
                                <Row>
                                    <Col xs="12" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Telefone Fixo</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.landline || ""}
                                        />
                                    </Col>
                                    <Col xs="12" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Telefone 2</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.telephone_02 || alunoCadastro?.telephone_01 || ""}
                                        />
                                    </Col>
                                    <Col xs="12" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Telefone 3</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.telephone_03 || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 9: Telefone 4, Telefone 5, Telefone 6 */}
                                <Row>
                                    <Col xs="12" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Telefone 4</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.telephone_04 || ""}
                                        />
                                    </Col>
                                    <Col xs="12" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Telefone 5</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.telephone_05 || ""}
                                        />
                                    </Col>
                                    <Col xs="12" md="4" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Telefone 6</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.telephone_06 || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 10: E-mail */}
                                <Row>
                                    <Col xs="12" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">E-mail</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.email || alunoDetalheItem.email || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 11: Facebook */}
                                <Row>
                                    <Col xs="12" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Facebook</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.facebook || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Linha 12: Instagram */}
                                <Row>
                                    <Col xs="12" className="mb-3">
                                        <Label className="form-control-label font-weight-bold small">Instagram</Label>
                                        <Input
                                            className="form-control-alternative bg-white"
                                            readOnly
                                            value={alunoCadastro?.instagram || ""}
                                        />
                                    </Col>
                                </Row>

                                {/* Item 13: Histórico de relatórios e vendedores do álbum */}
                                <div className="mt-4 pt-3 border-top">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h4 className="text-primary font-weight-bold mb-0" style={{ color: '#0d2b45', fontSize: '1.05rem' }}>
                                            <i className="fa-solid fa-book-open mr-2"></i>
                                            Histórico de relatórios e vendedores do álbum
                                        </h4>
                                        <Badge color="primary" pill className="px-3 py-1">
                                            {historicoAlbum.length} registro(s)
                                        </Badge>
                                    </div>

                                    {carregandoHistorico ? (
                                        <div className="text-center py-3 text-muted">
                                            <i className="fa-solid fa-spinner fa-spin mr-2"></i> Carregando histórico do álbum...
                                        </div>
                                    ) : historicoAlbum.length === 0 ? (
                                        <div className="p-3 text-center text-muted small bg-light rounded">
                                            Nenhum histórico de relatório ou vendedor registrado para o álbum deste formando.
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <Table size="sm" hover className="table-bordered text-center align-items-center mb-0">
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th>Data</th>
                                                        <th>Documento</th>
                                                        <th>Fotos</th>
                                                        <th>Kit</th>
                                                        <th>Status</th>
                                                        <th>Tipo</th>
                                                        <th>Dt. Dev.</th>
                                                        <th>Vendedor</th>
                                                        <th>Nome Vendedor</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historicoAlbum.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.data ? new Date(item.data).toLocaleDateString('pt-BR') : '-'}</td>
                                                            <td><strong>{item.documento || '-'}</strong></td>
                                                            <td>{item.fotos ?? 0}</td>
                                                            <td>{item.kit ? 'True' : 'False'}</td>
                                                            <td>
                                                                <span className="font-weight-bold" style={{ color: '#32325d' }}>
                                                                    {item.statusLabel || '-'}
                                                                </span>
                                                            </td>
                                                            <td>{item.tipo || '-'}</td>
                                                            <td>{item.dtDevolucao ? new Date(item.dtDevolucao).toLocaleDateString('pt-BR') : '-'}</td>
                                                            <td>{item.vendedorId != null ? String(item.vendedorId).padStart(6, '0') : '-'}</td>
                                                            <td>{item.vendedorNome || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </Card>
                    </div>
                )}
            </Container>

            <EditAlunoModal
                isOpen={editModalOpen}
                toggle={() => setEditModalOpen(false)}
                aluno={alunoParaEdicao}
                contratoId={contratoIdEdicao}
                cursoId={cursoIdEdicao}
                onAlunoAtualizado={handleAlunoAtualizado}
            />
        </>
    );
};

export default AlunoDetalhado;

